<template>
    <VJoyDrawer ref="drawer" :focus-trap="false" :elevated="true" size="large">
        <template #drawer-header>Admin Panel</template>
        <template #drawer-body>
            <div class="admin-panel-items">
                <h3>General</h3>
                <div class="admin-panel-item">
                    Score:
                    <strong>{{ selectedProfileAdminDetails?.score }}</strong>
                    &nbsp;
                    <VJoyButton variant="secondary" size="xsmall" @click="showScoreExplanation = !showScoreExplanation">Explain</VJoyButton>
                    <br />
                    <span v-if="showScoreExplanation" v-html="selectedProfileAdminDetails?.explanation" />
                </div>
                <div class="admin-panel-item">
                    Categories:
                    <strong>{{ selectedProfileAdminDetails?.categories }}</strong>
                </div>
                <div class="admin-panel-item">
                    Bonus NSE (Not seen enough):
                    <strong>{{ selectedProfileAdminDetails?.notSeenEnoughBonus }}</strong>
                </div>
                <div class="admin-panel-item">
                    Last profile modification date:
                    <strong>{{ formatDate(selectedProfileAdminDetails?.lastModificationDate) }}</strong>
                </div>
                <div class="admin-panel-item">
                    Last connection date:
                    <strong>{{ formatDate(selectedProfileAdminDetails?.lastConnectionDate) }}</strong>
                </div>
                <div v-if="selectedProfileAdminDetails?.alerts.length" class="admin-panel-item">
                    Profile Visibility Alerts:
                    <ul>
                        <li v-for="alert in selectedProfileAdminDetails?.alerts" :key="alert.message">
                            {{ alert.message }} {{ alert.makeProfileInvisible ? '(makes profile invisible)' : '' }}
                        </li>
                    </ul>
                </div>
                <div class="admin-panel-item">
                    <h3>Conversion rate</h3>
                    <p class="admin-panel-item">
                        Views / impressions and Conversations / views are no longer available. Contact the SRE team if you need them. This
                        freelancer's views and search appearances are on their visibility dashboard, linked as “Stats” in the admin section of their
                        profile page.
                    </p>
                    <table class="table-section">
                        <thead>
                            <tr>
                                <th>Quotations / conversations</th>
                                <th>Invoices / conversations</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td colspan="2" style="text-align: center"><strong>For 1 month</strong></td>
                            </tr>
                            <tr>
                                <td>
                                    {{ selectedProfileAdminDetails?.conversationQuotationRate?.month1?.fraction }}
                                    <br />
                                    <small>
                                        {{ selectedProfileAdminDetails?.conversationQuotationRate?.month1?.numerator }} out of
                                        {{ selectedProfileAdminDetails?.conversationQuotationRate?.month1?.denominator }}
                                    </small>
                                </td>
                                <td>
                                    {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month1?.fraction }}
                                    <br />
                                    <small>
                                        {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month1?.numerator }} out of
                                        {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month1?.denominator }}
                                    </small>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2" style="text-align: center"><strong>For 1 quarter</strong></td>
                            </tr>
                            <tr>
                                <td>
                                    {{ selectedProfileAdminDetails?.conversationQuotationRate?.month3?.fraction }}
                                    <br />
                                    <small>
                                        {{ selectedProfileAdminDetails?.conversationQuotationRate?.month3?.numerator }} out of
                                        {{ selectedProfileAdminDetails?.conversationQuotationRate?.month3?.denominator }}
                                    </small>
                                </td>
                                <td>
                                    {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month3?.fraction }}
                                    <br />
                                    <small>
                                        {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month3?.numerator }} out of
                                        {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month3?.denominator }}
                                    </small>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2" style="text-align: center"><strong>For 1 year</strong></td>
                            </tr>
                            <tr>
                                <td>
                                    {{ selectedProfileAdminDetails?.conversationQuotationRate?.month12?.fraction }}
                                    <br />
                                    <small>
                                        {{ selectedProfileAdminDetails?.conversationQuotationRate?.month12?.numerator }} out of
                                        {{ selectedProfileAdminDetails?.conversationQuotationRate?.month12?.denominator }}
                                    </small>
                                </td>
                                <td>
                                    {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month12?.fraction }}
                                    <br />
                                    <small>
                                        {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month12?.numerator }} out of
                                        {{ selectedProfileAdminDetails?.conversationInvoiceRate?.month12?.denominator }}
                                    </small>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </VJoyDrawer>
</template>

<script setup lang="ts">
    import {onMounted, onBeforeUnmount, ref, useTemplateRef} from 'vue';
    import {useLocale} from '#imports';
    import {VJoyDrawer, VJoyButton} from '@maltjoy/core-vue';
    import {eventBus} from '~/config/events';
    import type {AdminDetails} from '~/types/types';

    const {locale} = useLocale();

    const drawer = useTemplateRef('drawer');

    const selectedProfileAdminDetails = ref<AdminDetails>();

    const showScoreExplanation = ref<boolean>(false);

    function formatDate(date?: Date) {
        return new Intl.DateTimeFormat(locale.value, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour12: false,
        }).format(date);
    }

    async function onEvent(adminDetails: AdminDetails) {
        selectedProfileAdminDetails.value = adminDetails;
        await drawer.value?.show();
    }

    onMounted(() => eventBus.on('profile-card:admin:click', onEvent));
    onBeforeUnmount(() => eventBus.off('profile-card:admin:click', onEvent));
</script>

<style lang="scss" scoped>
    .admin-panel-items {
        display: flex;
        flex-direction: column;
    }

    .admin-panel-item {
        margin-bottom: var(--joy-core-spacing-5);
    }

    .table-section th {
        color: var(--joy-color-secondary-10);
        background-color: var(--joy-color-secondary-50);
    }

    .table-section td {
        border: 1px solid var(--joy-color-neutral-40);
    }
</style>
